<script>
// inheritAttrs:false so the wrapper controls attribute placement; an explicit
// prop (type/disabled) wins over the same attribute arriving via $attrs because
// v-bind="$attrs" is bound BEFORE the explicit bindings on the root element.
export default { inheritAttrs: false };
</script>

<script setup>
import { computed } from "vue";
import cn from "@/helpers/cn.js";
import BaseIcon from "@/Components/UI/BaseIcon.vue";

// Canonical button. `variant` + `size` drive the look; class merge via cn()
// (tailwind-merge only — write `cond && "class"`, never object syntax; props.class
// is merged LAST so a caller's class wins). No class-sniffing regex.
const props = defineProps({
    variant: {
        type: String,
        default: "primary",
        // primary | secondary | danger | ghost | neutral
        validator: (v) => ["primary", "secondary", "danger", "ghost", "neutral"].includes(v),
    },
    size: {
        type: String,
        default: "md",
        // sm | md | lg
        validator: (v) => ["sm", "md", "lg"].includes(v),
    },
    icon: String, // lucide name rendered via BaseIcon
    iconPosition: {
        type: String,
        default: "left",
        // left | right
        validator: (v) => ["left", "right"].includes(v),
    },
    loading: Boolean,
    disabled: Boolean,
    type: {
        type: String,
        default: "button",
        // button | submit | reset
        validator: (v) => ["button", "submit", "reset"].includes(v),
    },
    as: { type: [String, Object], default: "button" }, // element/component to render as
    block: Boolean, // w-full convenience
    square: Boolean, // icon-only square hit target
    left: Boolean, // left-align content instead of centering
    class: { default: "" },
    // legacy /storage glyph (transitional — migrate to `icon` lucide opportunistically)
    img: String,
    imgPosition: {
        type: String,
        default: "left",
        validator: (v) => ["left", "right"].includes(v),
    },
    component: { type: [String, Object], default: undefined }, // legacy alias for `as`
});

// Variant → KEDO token classes (halved Tailwind scale).
const VARIANTS = {
    primary: "bg-accent-blue text-white text-sm",
    // text-dark-2 is stated, not inherited: with as="a" this button renders as an anchor,
    // and a host page (the WordPress embed) paints its own link colour onto it otherwise.
    secondary:
        "border border-light-2 text-dark-2 transition-all duration-150 stroke-dark-2 fill-dark-2 hover:text-accent-blue hover:border-accent-blue hover:stroke-accent-blue hover:fill-accent-blue",
    // danger fills with red-2, not accent-red: white on accent-red is 3.38:1,
    // below AA. The bright accent-* variants are badge/dot fills carrying dark
    // ink — they are not a white-text surface. red-2 on white is 6.05:1.
    danger: "bg-red-2 text-white",
    ghost: "bg-transparent text-accent-blue hover:text-blue-1",
    // dark-2, not dark-light: this button is ACTIVE, so the 4.28:1 that WCAG
    // forgives on a disabled field is a real failure here. dark-2 is 16.16:1.
    neutral: "bg-light-3 text-dark-2 text-sm",
};
// size → height/padding (md = the legacy default).
const SIZES = {
    sm: "h-16 md:h-18 px-6 text-2xs",
    md: "h-20 md:h-22 px-8",
    lg: "h-22 md:h-24 px-10",
};
const SQUARE_SIZES = {
    sm: "h-16 md:h-18 w-16 md:w-18",
    md: "h-20 md:h-22 w-20 md:w-22",
    lg: "h-22 md:h-24 w-22 md:w-24",
};
// no-underline: a button never wants one, and with as="a" a host page's link styles apply.
const BASE =
    "flex font-semibold items-center gap-4 no-underline transition-all relative active:scale-99 rounded md:rounded-md";

const rootEl = computed(() => props.component ?? props.as);
const isDisabled = computed(() => props.disabled || props.loading);
const spinnerWidth = computed(() => ({ sm: 14, md: 18, lg: 20 })[props.size]);

// props.class LAST so caller overrides win (tailwind-merge).
const rootClass = computed(() =>
    cn(
        BASE,
        props.left ? "justify-start" : "justify-center",
        VARIANTS[props.variant],
        props.square ? SQUARE_SIZES[props.size] : SIZES[props.size],
        props.block && "w-full",
        isDisabled.value && "grayscale pointer-events-none opacity-50",
        props.class,
    ),
);

// The slot used to be gated on mount with a hardcoded "Отправить" fallback, because
// renderSlot crashed under SSR. That crash was a dual-Vue-instance problem, fixed at the
// root in vite.ssg.config.js (optimizeDeps.exclude), so the guard only survived as a
// vestige — and it made every button in the pre-hydration WordPress shell read
// "Отправить" instead of its real label, for the seconds that page takes to load.
</script>

<template>
    <component
        :is="rootEl"
        v-bind="$attrs"
        :type="type"
        :disabled="isDisabled"
        :class="rootClass">
        <!-- legacy /storage glyph keeps its middle layout -->
        <template v-if="img">
            <span v-if="imgPosition === 'right'">
                <slot />
            </span>
            <img
                :src="`/storage/images/buttons/${img}.svg`"
                alt="" />
            <span v-if="imgPosition === 'left'">
                <slot />
            </span>
        </template>
        <template v-else>
            <!-- currentColor so the spinner inherits the button text colour: white on
                 primary/danger/neutral, dark on ghost/secondary. Never hardcode white. -->
            <svg
                v-if="loading"
                class="bb-spinner shrink-0"
                :width="spinnerWidth"
                :height="spinnerWidth"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true">
                <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="9"
                    stroke="currentColor"
                    stroke-width="3" />
                <path
                    d="M21 12a9 9 0 0 0-9-9"
                    stroke="currentColor"
                    stroke-width="3"
                    stroke-linecap="round" />
            </svg>
            <BaseIcon
                v-else-if="icon && iconPosition === 'left'"
                :name="icon" />
            <slot />
            <BaseIcon
                v-if="!loading && icon && iconPosition === 'right'"
                :name="icon" />
        </template>
    </component>
</template>

<style scoped>
.bb-spinner {
    transform-origin: center;
    transform-box: fill-box;
    animation: bb-spin 0.6s linear infinite;
}
@keyframes bb-spin {
    to {
        transform: rotate(360deg);
    }
}
</style>
